import { apiFetch } from "@/lib/api";

export type PottingGrade = "A" | "B" | "C";

export interface FinalQcDevice {
  id: number;
  finishedSku: string | null;
  firmwareVersion: string | null;
  pcbaSerial: string | null;
  clampSerial: string | null;
  deviceLabelSerial: string;
  boxSerial: string | null;
  shipmentId: number | null;
  batchItemId: number | null;
  reasonNotInBatch: string | null;
  stationId: number | null;
  locationId: number | null;
  imei: string | null;
  batchId: number | null;
  batchLocationId: number | null;
  pottingPurchaseOrderItemId: number | null;
  hasSentPottingRow: boolean;
  existingPottingGrade: PottingGrade | null;
  requiresPottingGrade: boolean;
  pottingRework: {
    id: number;
    incidentId: number;
    routedAt: string | null;
    regradeNotes: string | null;
    regradePottingGrade: PottingGrade | "Fail" | null;
  } | null;
  touchSample: boolean;
  /** Repurposed ABB serials skip magnet reset and GPS watch. */
  skipWirelessChecks?: boolean;
}

export interface FailureReason {
  label: string;
  value: string;
  description: string | null;
}

export interface FinalQcResult {
  ok: boolean;
  reason: string;
  serial: string | null;
  shipment_id?: number | null;
  sent_to_pack_out?: boolean;
  created_shipment?: boolean;
  station_id?: number | null;
  potting_grade_rows?: number;
  pottingReworkId?: number;
  /** Present on potting_rework_passed when the server also ran the normal Final QC batch pass. */
  normal_pass_ok?: boolean;
  normal_pass_reason?: string;
  /** When potting rework passed with no shipment_id: why the hub did not assign a shipment. */
  potting_no_shipment_cause?: "alternate" | "quality_control_repair";
}

export interface FinalQcLaborSession {
  id: number;
  started_at: string;
  device_count?: number;
  net_seconds?: number;
}

export interface FinalQcLaborScan {
  device_serial: string;
  session_id: number;
}

export interface FinalQcLaborSessionResult {
  idle_timeout_ms: number;
  heartbeat_interval_ms: number;
  session: FinalQcLaborSession | null;
  scans: FinalQcLaborScan[];
  was_stale: boolean;
  active_on_other_page: string | null;
  writesBlocked?: boolean;
  writeFirebreak?: boolean;
}

export interface FinalQcLaborStartResult {
  session: FinalQcLaborSession | null;
  error?: string;
  page?: string;
  writesBlocked?: boolean;
  writeFirebreak?: boolean;
}

export interface FinalQcLaborEndResult {
  device_count?: number;
  net_seconds?: number;
  error?: string;
}

export interface FinalQcGpsCheckState {
  attemptCount: number;
  lastCheckStartedAt: string | null;
  passed: boolean;
  passSource: "portal" | "override" | null;
  qcIncidentId: number | null;
}

export interface FinalQcWirelessCheck {
  serial: string;
  addedAt: string;
  hit: "none" | "austin" | "other" | "bad";
  visualPassed: boolean;
  visualPassedAt: string | null;
  shipmentId: number | null;
  imei: string | null;
  serialFciId: number | null;
  gpsCheck: FinalQcGpsCheckState;
}

export interface FinalQcGpsCheckFailResult {
  ok: true;
  serial: string;
  hit: FinalQcWirelessCheck["hit"];
  recorded: boolean;
  attemptNumber: number | null;
  outcome: "fail" | null;
  cooldownBlocked: boolean;
  requireQcIncident: boolean;
  operatorMessage: string;
  lastCheckStartedAt: string | null;
  nextCheckAllowedAt: string | null;
  remainingMs: number;
  suggestedFailureKind: "check_in_failing" | "no_coordinates" | "bad_coordinates";
  suggestedFailureModeId: number | null;
  suggestedFailureModeName: string | null;
  episode: FinalQcGpsCheckState;
}

export interface FinalQcVisualPassResult {
  ok: boolean;
  reason: string;
  serial: string;
  visual_passed_at: string | null;
}

export type WirelessHitsSyncInfo =
  | { ok: true }
  | { ok: false; message: string };

export interface GpsCheckFailureModeRef {
  id: number;
  category: string;
  name: string;
}

export interface FinalQcWirelessChecksListResponse {
  rows: FinalQcWirelessCheck[];
  gpsFailureModes?: {
    check_in_failing: GpsCheckFailureModeRef | null;
    no_coordinates: GpsCheckFailureModeRef | null;
    bad_coordinates: GpsCheckFailureModeRef | null;
  };
}

export interface FinalQcWirelessHitsSyncResponse {
  wirelessHitsSync: WirelessHitsSyncInfo;
}

export const FINAL_QC_DEVICE_QUERY_KEY = ["manufacturing", "final-qc", "device"] as const;
export const FINAL_QC_FAILURE_REASONS_QUERY_KEY = ["manufacturing", "final-qc", "failure-reasons"] as const;
export const FINAL_QC_WIRELESS_CHECKS_QUERY_KEY = ["manufacturing", "final-qc", "wireless-checks"] as const;

export interface FinalQcWirelessGpsWatchState {
  required: boolean;
  enqueued: boolean;
  created: boolean;
}

export interface FinalQcDeviceLookupResponse {
  device: FinalQcDevice;
  wirelessGpsWatch?: FinalQcWirelessGpsWatchState;
}

export function fetchFinalQcDevice(
  serial: string,
  options?: { laborSessionId?: number },
): Promise<FinalQcDeviceLookupResponse> {
  return apiFetch<FinalQcDeviceLookupResponse>("/api/manufacturing/final-qc/device", {
    query: {
      serial,
      ...(options?.laborSessionId != null && options.laborSessionId > 0
        ? { laborSessionId: String(options.laborSessionId) }
        : {}),
    },
  });
}

export function fetchFinalQcFailureReasons(): Promise<{ rows: FailureReason[] }> {
  return apiFetch<{ rows: FailureReason[] }>("/api/manufacturing/final-qc/failure-reasons");
}

export function passFinalQc(input: {
  serial: string;
  potting_grade?: PottingGrade | null;
  laborSessionId?: number;
}): Promise<FinalQcResult> {
  return apiFetch<FinalQcResult>("/api/manufacturing/final-qc/pass", {
    method: "POST",
    body: input,
  });
}

export function visualPassFinalQc(serial: string): Promise<FinalQcVisualPassResult> {
  return apiFetch<FinalQcVisualPassResult>("/api/manufacturing/final-qc/visual-pass", {
    method: "POST",
    body: { serial },
  });
}

export function failFinalQc(input: {
  serial: string;
  issue: string;
  issue_notes?: string | null;
  potting_grade?: PottingGrade | null;
  laborSessionId?: number;
}): Promise<FinalQcResult> {
  return apiFetch<FinalQcResult>("/api/manufacturing/final-qc/fail", {
    method: "POST",
    body: input,
  });
}

export interface RevertPassAndFailResult {
  ok: boolean;
  reason: string;
  serials: string[];
  count: number;
}

export function revertPassAndFailFinalQc(input: {
  serials: string;
  issue: string;
  issue_notes?: string | null;
  potting_grade?: PottingGrade | null;
}): Promise<RevertPassAndFailResult> {
  return apiFetch<RevertPassAndFailResult>("/api/manufacturing/final-qc/revert-pass-and-fail", {
    method: "POST",
    body: {
      serials: input.serials,
      issue: input.issue,
      issue_notes: input.issue_notes,
      potting_grade: input.potting_grade,
    },
  });
}

export async function fetchFinalQcLaborSession(): Promise<FinalQcLaborSessionResult | null> {
  return apiFetch<FinalQcLaborSessionResult | null>("/api/labor/active-session", {
    query: { page: "final-qc", domain: "manufacturing" },
  });
}

export async function startFinalQcLaborSession(): Promise<FinalQcLaborStartResult> {
  return apiFetch<FinalQcLaborStartResult>("/api/labor/start-session", {
    method: "POST",
    body: { page: "final-qc", domain: "manufacturing" },
  });
}

export async function endFinalQcLaborSession(
  sessionId: number,
  idleTimeout: boolean,
): Promise<FinalQcLaborEndResult | null> {
  return apiFetch<FinalQcLaborEndResult | null>("/api/labor/end-session", {
    method: "POST",
    body: { sessionId, idleTimeout, domain: "manufacturing" },
  });
}

export async function sendFinalQcLaborHeartbeat(sessionId: number): Promise<void> {
  await apiFetch("/api/labor/heartbeat", {
    method: "POST",
    body: { sessionId, domain: "manufacturing" },
  });
}

export async function fetchFinalQcWirelessChecksList(): Promise<FinalQcWirelessChecksListResponse> {
  return apiFetch<FinalQcWirelessChecksListResponse>("/api/manufacturing/final-qc/wireless-checks", {
    cache: "no-store",
  });
}

export async function postFinalQcWirelessHitsSync(): Promise<FinalQcWirelessHitsSyncResponse> {
  return apiFetch<FinalQcWirelessHitsSyncResponse>("/api/manufacturing/final-qc/wireless-checks/sync", {
    method: "POST",
    body: {},
  });
}

/** Reset GPS watch after a factory reset (clears visual pass and hit). Normal scan enqueues watch via GET /device. */
export async function resetFinalQcWirelessGpsWatch(serial: string): Promise<{ ok: boolean; serial: string; reset?: boolean }> {
  return apiFetch<{ ok: boolean; serial: string; reset?: boolean }>("/api/manufacturing/final-qc/wireless-checks", {
    method: "POST",
    body: { serial },
  });
}

/** @deprecated Use resetFinalQcWirelessGpsWatch — scan enqueues GPS watch automatically. */
export async function watchFinalQcWirelessCheck(serial: string): Promise<{ ok: boolean; serial: string; reset?: boolean }> {
  return resetFinalQcWirelessGpsWatch(serial);
}

export async function removeFinalQcWirelessCheck(serial: string): Promise<void> {
  await apiFetch(`/api/manufacturing/final-qc/wireless-checks/${encodeURIComponent(serial)}`, {
    method: "DELETE",
  });
}

export async function failFinalQcGpsCheck(serial: string): Promise<FinalQcGpsCheckFailResult> {
  return apiFetch<FinalQcGpsCheckFailResult>("/api/manufacturing/final-qc/gps-check/fail", {
    method: "POST",
    body: { serial },
  });
}

export async function overrideFinalQcGpsCheck(input: {
  serial: string;
  mdmUserId: string;
  pin: string;
}): Promise<{ ok: true; serial: string; overrideUserDisplayName: string | null; occurredAt: string }> {
  return apiFetch("/api/manufacturing/final-qc/gps-check/override", {
    method: "POST",
    body: input,
  });
}

export async function linkFinalQcGpsCheckQcIncident(
  serial: string,
  incidentId: number,
): Promise<{ ok: true; serial: string }> {
  return apiFetch("/api/manufacturing/final-qc/gps-check/link-qc-incident", {
    method: "POST",
    body: { serial, incidentId },
  });
}
